import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Card, CardContent, Chip, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { Title } from 'react-admin';
import environment from '../lib/reactAppEnv';

type Entry = {
  ts: string;
  user: string;
  action: string;
  device: string;
  detail: string;
};

function parseLine(line: string): Entry {
  // format: 2026-04-21T15:46:45.796Z user=admin action device=uuid detail...
  const m = line.match(/^(\S+)\s+user=(\S+)\s+(\S+)\s+device=(\S+)\s*(.*)/);
  if (m) {
    const [, ts, user, action, device, detail] = m;
    return { ts, user, action, device, detail: detail.trim() };
  }
  return { ts: '', user: '?', action: line, device: '?', detail: '' };
}

const ACTION_COLORS: Record<string, 'error' | 'warning' | 'success' | 'info' | 'default'> = {
  reboot: 'warning',
  shutdown: 'error',
  blink: 'info',
  wol: 'success',
  'splash-logo': 'default',
};

const AuditLogPage: React.FC = () => {
  const [entries, setEntries] = React.useState<Entry[]>([]);
  const [loading, setLoading] = React.useState(false);
  const remoteBase = environment.REACT_APP_OPEN_BALENA_REMOTE_URL ?? '';

  const load = React.useCallback(() => {
    setLoading(true);
    fetch(`${remoteBase}/audit-log`)
      .then(r => r.json())
      .then((d: { entries: string[] }) => setEntries((d.entries ?? []).map(parseLine)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [remoteBase]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <Title title='Audit Log' />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h5'>Audit Log</Typography>
        <Tooltip title='Vernieuwen'>
          <span>
            <IconButton onClick={load} disabled={loading} size='small'>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Tijd</TableCell>
                <TableCell sx={{ width: 160 }}>Gebruiker</TableCell>
                <TableCell sx={{ width: 90 }}>Actie</TableCell>
                <TableCell sx={{ width: 260 }}>Device</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'text.disabled', py: 4 }}>
                    {loading ? 'Laden…' : 'Geen entries'}
                  </TableCell>
                </TableRow>
              )}
              {entries.map((e, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {new Date(e.ts).toLocaleString('nl-NL')}
                  </TableCell>
                  <TableCell>
                    <Chip label={e.user} size='small' variant='outlined' sx={{ fontSize: '12px' }} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={e.action}
                      size='small'
                      color={ACTION_COLORS[e.action] ?? 'default'}
                      sx={{ fontSize: '12px' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '11px' }}>{e.device}</TableCell>
                  <TableCell sx={{ fontSize: '12px', color: 'text.secondary' }}>{e.detail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuditLogPage;
